/**
 * The main menu, React edition of ui/MainMenu.ts.
 *
 * Same two-level shape and the same reasoning: the top level is a list of
 * destinations, the controls live one level down, and inside Settings the
 * panes fold as an accordion rather than a third flyout column. The pressed
 * states are projections of the lighting/quality stores now, so the menu can
 * never disagree with the scene.
 */

import { useEffect, useRef, useState } from 'react'
import {
  hostedRoutingEnabled,
  providerChoice,
  PROVIDERS,
  setProvider,
  type ProviderChoice,
} from '../../assistant/llm'
import { clearStoredSession } from '../../learn/SessionStore'
import { leaveToEntrance } from './entrance'
import { LIGHTING_PRESETS, setLighting } from '../../fab/lighting'
import { setQuality, type QualityChoice } from '../../fab/quality'
import { setSoundMuted } from '../../fab/sound'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { useLighting, useQuality, useSoundMuted } from '../state/hooks'
import { FabsMenu } from './FabsMenu'

type LeafId = 'model' | 'lighting' | 'graphics' | 'sound'

interface OptionRow {
  key: string
  label: string
  note: string
  on: boolean
  pick: () => void
}

function Options({ options }: { options: OptionRow[] }) {
  return (
    <div className="mainmenu-section">
      <div className="mainmenu-options">
        {options.map((o) => (
          <button
            key={o.key}
            className={'mainmenu-option' + (o.on ? ' on' : '')}
            role="menuitemradio"
            aria-checked={o.on}
            onClick={o.pick}
          >
            <span className="mainmenu-optlabel">{o.label}</span>
            <span className="mainmenu-optnote">{o.note}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function ModelPane() {
  // The provider store has no subscription of its own; this component is its
  // only writer, so a local mirror keeps the pressed state honest.
  const [choice, setChoice] = useState<ProviderChoice | null>(providerChoice())

  if (!hostedRoutingEnabled()) {
    return (
      <div className="mainmenu-section">
        <div className="mainmenu-note">{t('routing.OnDevice')}</div>
        <div className="mainmenu-note">{t('menu.LocalNote')}</div>
      </div>
    )
  }

  const rows: Array<{ value: ProviderChoice; label: string; note: string }> = [
    { value: 'auto', label: t('menu.Auto'), note: t('menu.AutoNote') },
    { value: 'openai', label: PROVIDERS.openai.label, note: PROVIDERS.openai.model },
    { value: 'deepseek', label: PROVIDERS.deepseek.label, note: PROVIDERS.deepseek.model },
  ]
  const active = choice ?? 'auto'
  return (
    <Options
      options={rows.map((r) => ({
        key: r.value,
        label: r.label,
        note: r.note,
        on: r.value === active,
        pick: () => {
          setProvider(r.value === 'auto' ? null : r.value)
          setChoice(providerChoice())
        },
      }))}
    />
  )
}

function LightingPane() {
  const active = useLighting()
  return (
    <Options
      options={LIGHTING_PRESETS.map((p) => ({
        key: p.id,
        label: t(p.labelKey),
        note: t(p.noteKey),
        on: p.id === active,
        pick: () => setLighting(p.id),
      }))}
    />
  )
}

function GraphicsPane() {
  const active = useQuality()
  const rows: Array<{ value: QualityChoice; label: string; note: string }> = [
    { value: 'auto', label: tOr('menu.QualityAuto', 'Automatic'), note: tOr('menu.QualityAutoNote', 'default') },
    { value: 'high', label: tOr('menu.QualityHigh', 'More lights'), note: tOr('menu.QualityHighNote', 'more realistic') },
    { value: 'low', label: tOr('menu.QualityLow', 'Less lights'), note: tOr('menu.QualityLowNote', 'less realistic') },
  ]
  return (
    <Options
      options={rows.map((r) => ({
        key: r.value,
        label: r.label,
        note: r.note,
        on: r.value === active,
        pick: () => setQuality(r.value),
      }))}
    />
  )
}

function SoundPane() {
  const muted = useSoundMuted()
  const rows: Array<{ value: boolean; label: string; note: string }> = [
    { value: false, label: tOr('menu.SoundOn', 'On'), note: tOr('menu.SoundOnNote', 'the hall as it sounds') },
    { value: true, label: tOr('menu.SoundOff', 'Muted'), note: tOr('menu.SoundOffNote', 'silent — M toggles') },
  ]
  return (
    <Options
      options={rows.map((r) => ({
        key: String(r.value),
        label: r.label,
        note: r.note,
        on: r.value === muted,
        pick: () => setSoundMuted(r.value),
      }))}
    />
  )
}

export function MainMenuControl() {
  useLang()
  const [open, setOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [leaf, setLeaf] = useState<LeafId | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const close = (): void => {
    setOpen(false)
    setSettingsOpen(false)
    setLeaf(null)
  }

  // Dismissal, registered only while the menu is up. Escape unwinds one
  // level at a time so a student never loses the whole menu for closing a
  // pane they had opened.
  useEffect(() => {
    if (!open) return
    const onDocPointer = (e: Event): void => {
      if (!rootRef.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Escape') return
      e.stopPropagation()
      setLeaf((l) => {
        if (l) return null
        setSettingsOpen((s) => {
          if (s) return false
          setOpen(false)
          return s
        })
        return l
      })
    }
    document.addEventListener('pointerdown', onDocPointer, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDocPointer, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const leafRow = (id: LeafId, label: string) => {
    const on = leaf === id
    return (
      <button
        className={'mainmenu-item' + (on ? ' on' : '')}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={on}
        onClick={() => setLeaf(on ? null : id)}
      >
        <span>{label}</span>
        <span className="mainmenu-chevron">{on ? '⌄' : '›'}</span>
      </button>
    )
  }

  return (
    <div
      ref={rootRef}
      className="mainmenu"
      // The entry screen is one big click target for walking into the fab.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className={'mainmenu-trigger' + (open ? ' on' : '')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {t('menu.Menu')}
      </button>
      {open && (
        <div className="mainmenu-panel" role="menu">
          {/* Hand this machine to the next student. The reload is what
              actually forgets - clearing storage alone would leave the old
              shift alive in memory, quietly re-saving itself. */}
          <button
            className="mainmenu-item"
            role="menuitem"
            title={tOr(
              'menu.SessionNote',
              'Your shift — journal, lot and tool settings — is saved on this machine and survives a reload.',
            )}
            onClick={() => {
              clearStoredSession()
              location.reload()
            }}
          >
            {tOr('menu.SessionClear', 'Start a new session')}
          </button>
          {/* Back out through the airlock, in place: the hall is already
              built, so the entry screen simply comes back up over it. The
              shift is kept - only the saved place is forgotten. */}
          <button
            className="mainmenu-item"
            role="menuitem"
            title={tOr(
              'menu.EntranceNote',
              'Return to the entry screen. Your journal, lot and tool settings are kept.',
            )}
            onClick={() => {
              close()
              leaveToEntrance()
            }}
          >
            {tOr('menu.Entrance', 'Back to entrance')}
          </button>
          {/* Fab A, Fab B, the floor you are standing in - and then the
              library: your own fabs, kept with your account. */}
          <FabsMenu onPick={close} />
          <div className="mainmenu-row">
            <button
              className={'mainmenu-item' + (settingsOpen ? ' on' : '')}
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={settingsOpen}
              onClick={() => {
                setSettingsOpen(!settingsOpen)
                setLeaf(null)
              }}
            >
              <span>{t('menu.Settings')}</span>
              <span className="mainmenu-chevron">›</span>
            </button>
            {settingsOpen && (
              <div className="mainmenu-submenu" role="menu">
                {leafRow('model', t('menu.Model'))}
                {leaf === 'model' && (
                  <div className="mainmenu-leaf">
                    <ModelPane />
                  </div>
                )}
                {leafRow('lighting', t('menu.Lighting'))}
                {leaf === 'lighting' && (
                  <div className="mainmenu-leaf">
                    <LightingPane />
                  </div>
                )}
                {leafRow('graphics', tOr('menu.Graphics', 'Graphics'))}
                {leaf === 'graphics' && (
                  <div className="mainmenu-leaf">
                    <GraphicsPane />
                  </div>
                )}
                {leafRow('sound', tOr('menu.Sound', 'Sound'))}
                {leaf === 'sound' && (
                  <div className="mainmenu-leaf">
                    <SoundPane />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
